/**
 * TinyMCE Editor - High-level wrapper for TinyMCE rich text editing
 *
 * Provides a simple API for initializing TinyMCE editors across the application.
 * Drop-in replacement for the custom RichTextEditor.
 */

import tinyMCELoader from '../utils/TinyMCELoader';

class TinyMCEEditor {
    /**
     * Register a selector for auto-initialization (preferred method)
     * @param {string} selector - CSS selector for textarea(s)
     * @param {object} config - Editor configuration options
     */
    static register(selector, config = {}) {
        tinyMCELoader.register(selector, config);
    }

    /**
     * Setup auto-initialization for editors matching a selector
     * @deprecated Use register() instead
     * @param {string} selector - CSS selector for textarea(s)
     * @param {object} config - Editor configuration options
     */
    static setupAutoInit(selector, config = {}) {
        tinyMCELoader.setupAutoInit(selector, config);
    }

    /**
     * Initialize editors on a selector
     * @param {string} selector - CSS selector for element(s)
     * @param {object} config - Editor configuration
     * @returns {Promise<Array>} Array of editor instances
     */
    static async init(selector, config = {}) {
        return tinyMCELoader.init(selector, config);
    }

    /**
     * Remove all editor instances
     */
    static removeAll() {
        tinyMCELoader.removeAll();
    }

    /**
     * Remove a specific editor instance
     * @param {string} id - Editor ID
     */
    static remove(id) {
        tinyMCELoader.remove(id);
    }

    /**
     * Get an editor instance
     * @param {string} id - Editor ID
     * @returns {object|null} Editor instance
     */
    static getInstance(id) {
        return tinyMCELoader.getInstance(id);
    }

    /**
     * Update variables for a specific editor
     * @param {string} id - Editor ID
     * @param {object} newVariables - New variables object
     * @returns {boolean} Success status
     */
    static updateVariables(id, newVariables) {
        return tinyMCELoader.updateEditorVariables(id, newVariables);
    }

    /**
     * Setup Turbo compatibility
     */
    static setupTurboCompatibility() {
        tinyMCELoader.setupTurboCompatibility();
    }

    /**
     * Setup accordion compatibility
     * @param {string} selector - CSS selector for elements
     * @param {object} config - Editor configuration
     */
    static setupAccordionCompatibility(selector, config = {}) {
        tinyMCELoader.setupAccordionCompatibility(selector, config);
    }
}

export default TinyMCEEditor;
